import { ChangeDetectorRef, Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { AbstractControl, FormArray, FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { NetzTranslateService } from 'src/app/services/netz-translate.service';
import { addRemoveAnimation } from 'src/app/+shared/animations/addRemove.animation';
import { CommonService } from 'src/app/services/common.service';
import { ccdTypes, DeviceUnitFormModel, LookupKeys } from '../technical-data.domain';
import { LookupOptionsData } from 'src/app/jurisdiction/jurisdiction.domain';
import { createEmptyFileInputFormValue } from 'src/app/+custom-controls/components/file-input-form-control/file-input-form-control.component';
import { SectionIdentifier } from 'src/app/+shared/components/review-section/review-section.domain';
import { Translations } from './drr-ccd-device-units.models';

@Component({
  selector: 'app-drr-ccd-device-units',
  templateUrl: './drr-ccd-device-units.component.html',
  styleUrls: ['./drr-ccd-device-units.component.scss'],
  animations: [addRemoveAnimation],
})
export class DrrCcdDeviceUnitsComponent implements OnInit {
  form: FormGroup;
  translations: Translations;

  loadManagementTypeOptions: LookupOptionsData[];
  couplingTypeOptions: LookupOptionsData[];
  SectionIdentifier = SectionIdentifier;

  private unitPowerFieldNames = {
    electricalPower: 'electricalPower',
    powerOfAdditionalHeater: 'powerOfAdditionalHeater',
  };

  private subUnitPowerFieldNames = {
    electricalPower: 'electricalPower',
    powerOfAdditionalHeater: 'powerOfAdditionalHeater',
  };

  private _cddType: string;
  get ccdType(): string {
    return this._cddType;
  }

  @Input() set ccdType(value: string) {
    this._cddType = value;
    if (this.form) {
      for (const unitForm of this.units.controls) {
        this.toggleAllControls(this.unitControls, unitForm as FormGroup, unitForm.value);
      }
      this.addDefaultUnit();
    }
    this.setTranslations();
  }
  @Input() data: DeviceUnitFormModel[];
  @Input() applicationId: any;
  @Input() isDetailsView: boolean;
  @Input() allowAddUnit: boolean = true;
  @Input() allowAddSubUnit: boolean = true;

  @Output() formInited = new EventEmitter<FormGroup>();

  constructor(
    private fb: FormBuilder,
    private cd: ChangeDetectorRef,
    private translation: NetzTranslateService,
    private commonService: CommonService
  ) {
    this.setTranslations();
  }

  ngOnInit(): void {
    this.form = this.fb.group({
      units: this.fb.array([]),
    });
    this.patchFormData();
    this.formInited.emit(this.form);
    this.loadLookups();

    this.translation.registerForLanguageChange().subscribe(_ => {
      this.setTranslations();
    });
  }

  private patchFormData() {
    if (this.data) {
      this.data.forEach(unit => {
        this.addUnit(unit);
      });
    }
  }

  private loadLookups() {
    this.loadManagementTypeOptions = this.commonService.getDropdownData(LookupKeys.LoadManagementType);
    this.couplingTypeOptions = this.commonService.getDropdownData(LookupKeys.CouplingType);
  }

  private addDefaultUnit() {
    if (!this.data && this.units.controls.length === 0) {
      this.addUnit(null);
    }
  }

  get units(): FormArray {
    return this.form.controls.units as FormArray;
  }

  showUploadUnitDataSheet(index: number): boolean {
    if (['heat_pump', 'air_conditioner'].includes(this.ccdType)) {
      return this.units.controls[index].get('hasAdditionalHeating')?.value;
    } else if (['wall_box'].includes(this.ccdType)) {
      return this.units.controls[index].get('loadManagementAvailable')?.value;
    }
    return false;
  }

  showUploadSubUnitDataSheet(unit: FormGroup, index: number): boolean {
    const subUnits = unit.controls.subUnits as FormArray;
    if (['heat_pump', 'air_conditioner'].includes(this.ccdType)) {
      return subUnits.controls[index].get('hasAdditionalHeating')?.value;
    } else if (['wall_box'].includes(this.ccdType)) {
      return subUnits.controls[index].get('loadManagementAvailable')?.value;
    }
    return false;
  }

  showLoadManagementType(index: number): boolean {
    if (['wall_box'].includes(this.ccdType)) {
      return this.units.controls[index].get('loadManagementAvailable')?.value;
    }
    return false;
  }

  get showUploadDataSheetStorage(): boolean {
    return ['storage'].includes(this.ccdType);
  }

  get showUploadCircuitDiagram(): boolean {
    return ['storage'].includes(this.ccdType);
  }

  showUploadDataSheetWr(index: number): boolean {
    return ['storage'].includes(this.ccdType) && this.units.controls[index].get('couplingType').value === 'ac';
  }

  get unitAddLabel(): string {
    let label: string;
    switch (this.ccdType) {
      case ccdTypes.storage:
        label = 'drr-ccd-devices.add-storage-btn';
        break;
      case ccdTypes.wallBox:
        label = 'drr-ccd-devices.add-wallbox-btn';
        break;
      case ccdTypes.heatPump:
        label = 'drr-ccd-devices.add-inner-unit-btn';
        break;
      case ccdTypes.airConditioner:
        label = 'drr-ccd-devices.add-outer-unit-btn';
        break;
      default:
        label = 'drr-ccd-devices.add-btn';
    }
    return this.translation.translateString(label);
  }

  getSubUnitLabel(num: number): string {
    let label = 'drr-ccd-devices.add-sub-btn';
    if (this.ccdType === ccdTypes.heatPump) {
      label = 'drr-ccd-devices.add-sub-outer-unit-to-btn';
    } else if (this.ccdType === ccdTypes.airConditioner) {
      label = 'drr-ccd-devices.add-sub-inner-unit-to-btn';
    }
    return this.translation.translateString(label, { innerUnit: num });
  }

  addUnit(unit: DeviceUnitFormModel): FormGroup {
    const unitFormControl = this.fb.group({});
    this.addAllControls(this.unitControls, unitFormControl);
    unitFormControl.addControl('unitTotalPower', this.fb.control(0));

    if (unit) {
      unitFormControl.patchValue(unit);
      unitFormControl.addControl('dataSheetRawValue', this.fb.control(unit.dataSheet));
      unitFormControl.addControl('dataSheetRwRawValue', this.fb.control(unit.dataSheetWr));
      unitFormControl.addControl('circuitDiagramRawValue', this.fb.control(unit.circuitDiagramDocument));

      for (const subUnit of unit.subUnits) {
        this.addSubUnit(unitFormControl, subUnit);
      }
    }

    this.toggleAllControls(this.unitControls, unitFormControl, unit);
    this.setDisabledForSubUnits(unit?.unavailable, unitFormControl.controls.subUnits as FormArray);
    this.setDisabledForUnit(unitFormControl);
    unitFormControl.controls.unavailable.valueChanges.subscribe(value => {
      this.toggleAllControls(this.unitControls, unitFormControl, { unavailable: value });
      this.setDisabledForSubUnits(value, unitFormControl.controls.subUnits as FormArray);
    });
    unitFormControl.controls.hasAdditionalHeating.valueChanges.subscribe(value => {
      this.toggleAllControls({ dataSheet: this.unitControls._dataSheet }, unitFormControl, { hasAdditionalHeating: value });
    });
    unitFormControl.controls.couplingType.valueChanges.subscribe(value => {
      this.toggleAllControls({ dataSheetWr: this.unitControls._dataSheetWr }, unitFormControl, { couplingType: value });
    });
    unitFormControl.controls.loadManagementAvailable.valueChanges.subscribe(value => {
      this.toggleAllControls(
        { dataSheet: this.unitControls._dataSheet, loadManagementType: this.unitControls.loadManagementType },
        unitFormControl,
        { loadManagementAvailable: value }
      );
    });
    unitFormControl.valueChanges.subscribe(_ => {
      this.calculateUnitTotalPower(unitFormControl);
    });

    (this.form.controls.units as FormArray).push(unitFormControl);

    // subunit is added in the form change events

    return unitFormControl;
  }

  addSubUnit(parentUnit: FormGroup, subUnit?: DeviceUnitFormModel) {
    const subUnitFormControl = this.fb.group({});
    this.addAllControls(this.subUnitControls, subUnitFormControl);
    if (subUnit) {
      subUnitFormControl.addControl('dataSheetRawValue', this.fb.control(subUnit.dataSheet));
      subUnitFormControl.patchValue(subUnit);
    }

    this.toggleAllControls(this.subUnitControls, subUnitFormControl, subUnit);
    subUnitFormControl.controls.unavailable.valueChanges.subscribe(value => {
      this.toggleAllControls(this.subUnitControls, subUnitFormControl, { unavailable: value });
      this.setDisabledForUnit(parentUnit);
    });
    subUnitFormControl.controls.hasAdditionalHeating.valueChanges.subscribe(value => {
      this.toggleAllControls({ dataSheet: this.subUnitControls._dataSheet }, subUnitFormControl, { hasAdditionalHeating: value });
    });

    if (parentUnit.controls.unavailable.value) {
      subUnitFormControl.controls.unavailable.disable({ emitEvent: false });
    }
    if (parentUnit.controls.subUnits) {
      const subUnits = parentUnit.controls.subUnits as FormArray;
      subUnits.controls.push(subUnitFormControl);
      this.setDisabledForSubUnits(parentUnit.controls.unavailable.value, subUnits);

      subUnitFormControl.valueChanges.subscribe(_ => {
        subUnits.updateValueAndValidity();
      });
    }
  }

  fileFormInited(file: FormGroup, parentForm: FormGroup, controlName: string) {
    parentForm.addControl(controlName, file);
    parentForm.updateValueAndValidity();
    this.cd.detectChanges();
  }

  removeUnit(index: number) {
    (this.form.controls.units as FormArray).removeAt(index);
    this.form.updateValueAndValidity();
  }

  removeSubUnit(unit: FormGroup, index: number) {
    const subUnits = unit.controls.subUnits as FormArray;
    subUnits.removeAt(index);
    this.setDisabledForSubUnits(unit.controls.unavailable.value, subUnits);
    this.form.updateValueAndValidity();
  }

  unitControls = {
    id: {
      add: form => form.addControl('id', this.fb.control(null)),
      visible: false,
      toggle: _ => {},
    },
    manufacturer: {
      visible: false,
      add: form => form.addControl('manufacturer', this.fb.control(null, [Validators.required])),
      toggle: (form: FormGroup, value: DeviceUnitFormModel) => {
        this.unitControls.manufacturer.visible = true;
        this.disableIfHidden(this.unitControls.manufacturer, form.controls.manufacturer);
        if (this.unitControls.manufacturer.visible) {
          this.toggleUnavailable(form.controls.manufacturer as FormControl, value?.unavailable);
        }
      },
      onUnavailable: (form, isUnavailable) => this.toggleUnavailable(form.controls.manufacturer, isUnavailable),
    },
    designationType: {
      visible: false,
      add: form => form.addControl('designationType', this.fb.control(null, [Validators.required])),
      toggle: (form: FormGroup, value: DeviceUnitFormModel) => {
        this.unitControls.designationType.visible = true;
        this.disableIfHidden(this.unitControls.designationType, form.controls.designationType);
        if (this.unitControls.designationType.visible) {
          this.toggleUnavailable(form.controls.designationType as FormControl, value?.unavailable);
        }
      },
    },
    electricalPower: {
      visible: false,
      add: form => form.addControl('electricalPower', this.fb.control(null, [Validators.required])),
      toggle: (form: FormGroup, value: DeviceUnitFormModel) => {
        this.unitControls.electricalPower.visible = true;
        this.disableIfHidden(this.unitControls.electricalPower, form.controls.electricalPower);
        if (this.unitControls.electricalPower.visible) {
          this.toggleUnavailable(form.controls.electricalPower as FormControl, value?.unavailable);
        }
      },
    },
    powerOfAdditionalHeater: {
      visible: false,
      add: form => form.addControl('powerOfAdditionalHeater', this.fb.control(null, [Validators.required])),
      toggle: (form: FormGroup, value: DeviceUnitFormModel) => {
        this.unitControls.powerOfAdditionalHeater.visible = ['heat_pump', 'air_conditioner'].includes(this.ccdType);
        this.disableIfHidden(this.unitControls.powerOfAdditionalHeater, form.controls.powerOfAdditionalHeater);
        if (this.unitControls.powerOfAdditionalHeater.visible) {
          this.toggleUnavailable(form.controls.powerOfAdditionalHeater as FormControl, value?.unavailable);
        }
      },
    },
    hasAdditionalHeating: {
      visible: false,
      add: form => form.addControl('hasAdditionalHeating', this.fb.control(null, [Validators.required])),
      toggle: (form: FormGroup, value: DeviceUnitFormModel) => {
        this.unitControls.hasAdditionalHeating.visible = ['heat_pump', 'air_conditioner'].includes(this.ccdType);
        this.disableIfHidden(this.unitControls.hasAdditionalHeating, form.controls.hasAdditionalHeating);
        if (this.unitControls.hasAdditionalHeating.visible) {
          this.toggleUnavailable(form.controls.hasAdditionalHeating as FormControl, value?.unavailable);
        }
      },
    },
    unavailable: {
      visible: false,
      label: () =>
        ['heat_pump'].includes(this.ccdType)
          ? this.translation.translateString('drr-ccd-devices.inner-unit-unavailable-lbl')
          : this.translation.translateString('drr-ccd-devices.outer-unit-unavailable-lbl'),
      add: form => form.addControl('unavailable', this.fb.control(null, [])),
      toggle: (form: FormGroup, value: DeviceUnitFormModel) => {
        this.unitControls.unavailable.visible = ['heat_pump', 'air_conditioner'].includes(this.ccdType);
        this.disableIfHidden(this.unitControls.unavailable, form.controls.unavailable);
      },
    },
    loadManagementAvailable: {
      add: form => form.addControl('loadManagementAvailable', this.fb.control(null, [Validators.required])),
      visible: false,
      toggle: (form: FormGroup, value: DeviceUnitFormModel) => {
        this.unitControls.loadManagementAvailable.visible = ['wall_box'].includes(this.ccdType);
        this.disableIfHidden(this.unitControls.loadManagementAvailable, form.controls.loadManagementAvailable);
        if (this.unitControls.loadManagementAvailable.visible) {
          this.toggleUnavailable(form.controls.loadManagementAvailable as FormControl, value?.unavailable);
        }
      },
    },
    loadManagementType: {
      visible: false,
      add: form => form.addControl('loadManagementType', this.fb.control(null, [Validators.required])),
      toggle: (form: FormGroup, value: DeviceUnitFormModel) => {
        this.unitControls.loadManagementType.visible = ['wall_box'].includes(this.ccdType) && value?.loadManagementAvailable;
        this.disableIfHidden(this.unitControls.loadManagementType, form.controls.loadManagementType);
        if (this.unitControls.loadManagementType.visible) {
          this.toggleUnavailable(form.controls.loadManagementType as FormControl, value?.unavailable);
        }
      },
    },
    couplingType: {
      visible: false,
      add: form => form.addControl('couplingType', this.fb.control(null, [Validators.required])),
      toggle: (form: FormGroup, value: DeviceUnitFormModel) => {
        this.unitControls.couplingType.visible = ['storage'].includes(this.ccdType);
        this.disableIfHidden(this.unitControls.couplingType, form.controls.couplingType);
        if (this.unitControls.couplingType.visible) {
          this.toggleUnavailable(form.controls.couplingType as FormControl, value?.unavailable);
        }
      },
    },
    subUnits: {
      visible: false,
      add: form => form.addControl('subUnits', this.fb.array([])),
      toggle: (form: FormGroup, value: DeviceUnitFormModel) => {
        const isSubUnitsVisible = ['heat_pump', 'air_conditioner'].includes(this.ccdType);
        this.unitControls.subUnits.visible = isSubUnitsVisible;
        if (isSubUnitsVisible && !form.getRawValue().subUnits.length) {
          this.addSubUnit(form);
        } else if (!isSubUnitsVisible && form.getRawValue().subUnits.length) {
          (form.controls.subUnits as FormArray).clear();
        }

        for (const subUnit of (form.controls.subUnits as FormArray).controls) {
          this.toggleAllControls(this.subUnitControls, subUnit as FormGroup, subUnit.value);
        }
      },
    },
    _dataSheet: {
      add: _ => {},
      default: createEmptyFileInputFormValue('drr_ccd_data_sheet'),
      visible: false,
      toggle: (form: FormGroup, value: DeviceUnitFormModel) => {
        if (value?.hasAdditionalHeating != null) {
          this.unitControls._dataSheet.visible = value?.hasAdditionalHeating;
          this.removeIfHiddenFileUpload(this.unitControls._dataSheet, form, 'dataSheet');
        } else if (value?.loadManagementAvailable != null) {
          this.unitControls._dataSheet.visible = value?.loadManagementAvailable;
          this.removeIfHiddenFileUpload(this.unitControls._dataSheet, form, 'dataSheet');
        }
      },
    },
    _dataSheetWr: {
      add: _ => {},
      default: createEmptyFileInputFormValue('drr_ccd_data_sheet_wr'),
      visible: false,
      toggle: (form: FormGroup, value: DeviceUnitFormModel) => {
        if (value?.couplingType != null) {
          this.unitControls._dataSheetWr.visible = value?.couplingType === 'ac';
          this.removeIfHiddenFileUpload(this.unitControls._dataSheetWr, form, 'dataSheetWr');
        }
      },
    },
    _circuitDiagramDocument: {
      add: _ => {},
      default: createEmptyFileInputFormValue('drr_ccd_circuit_diagram'),
      visible: false,
      toggle: (form: FormGroup, value: DeviceUnitFormModel) => {
        form.controls.circuitDiagramDocument &&
          this.disableIfHidden(this.unitControls._circuitDiagramDocument, form.controls.circuitDiagramDocument);
      },
    },
  };

  subUnitControls = {
    id: {
      add: form => form.addControl('id', this.fb.control(null)),
      visible: false,
      toggle: _ => {},
    },
    manufacturer: {
      visible: false,
      add: form => form.addControl('manufacturer', this.fb.control(null, [Validators.required])),
      toggle: (form: FormGroup, value: DeviceUnitFormModel) => {
        this.subUnitControls.manufacturer.visible = true;
        this.disableIfHidden(this.subUnitControls.manufacturer, form.controls.manufacturer);
        if (this.subUnitControls.manufacturer.visible) {
          this.toggleUnavailable(form.controls.manufacturer as FormControl, value?.unavailable);
        }
      },
    },
    designationType: {
      visible: false,
      add: form => form.addControl('designationType', this.fb.control(null, [Validators.required])),
      toggle: (form: FormGroup, value: DeviceUnitFormModel) => {
        this.subUnitControls.designationType.visible = true;
        this.disableIfHidden(this.subUnitControls.designationType, form.controls.designationType);
        if (this.subUnitControls.designationType.visible) {
          this.toggleUnavailable(form.controls.designationType as FormControl, value?.unavailable);
        }
      },
    },
    electricalPower: {
      visible: false,
      add: form => form.addControl('electricalPower', this.fb.control(null, [Validators.required])),
      toggle: (form: FormGroup, value: DeviceUnitFormModel) => {
        this.subUnitControls.electricalPower.visible = true;
        this.disableIfHidden(this.subUnitControls.electricalPower, form.controls.electricalPower);
        if (this.subUnitControls.electricalPower.visible) {
          this.toggleUnavailable(form.controls.electricalPower as FormControl, value?.unavailable);
        }
      },
    },
    powerOfAdditionalHeater: {
      visible: false,
      add: form => form.addControl('powerOfAdditionalHeater', this.fb.control(null, [Validators.required])),
      toggle: (form: FormGroup, value: DeviceUnitFormModel) => {
        this.subUnitControls.powerOfAdditionalHeater.visible = ['heat_pump', 'air_conditioner'].includes(this.ccdType);
        this.disableIfHidden(this.subUnitControls.powerOfAdditionalHeater, form.controls.powerOfAdditionalHeater);
        if (this.subUnitControls.powerOfAdditionalHeater.visible) {
          this.toggleUnavailable(form.controls.powerOfAdditionalHeater as FormControl, value?.unavailable);
        }
      },
    },
    hasAdditionalHeating: {
      visible: false,
      add: form => form.addControl('hasAdditionalHeating', this.fb.control(null, [Validators.required])),
      toggle: (form: FormGroup, value: DeviceUnitFormModel) => {
        this.subUnitControls.hasAdditionalHeating.visible = ['heat_pump', 'air_conditioner'].includes(this.ccdType);
        this.disableIfHidden(this.subUnitControls.hasAdditionalHeating, form.controls.hasAdditionalHeating);
        if (this.subUnitControls.hasAdditionalHeating.visible) {
          this.toggleUnavailable(form.controls.hasAdditionalHeating as FormControl, value?.unavailable);
        }
      },
    },
    unavailable: {
      visible: false,
      disabled: false,
      label: () =>
        ['heat_pump'].includes(this.ccdType)
          ? this.translation.translateString('drr-ccd-devices.outer-unit-unavailable-lbl')
          : this.translation.translateString('drr-ccd-devices.inner-unit-unavailable-lbl'),
      add: form => form.addControl('unavailable', this.fb.control(null, [])),
      toggle: (form: FormGroup, value: DeviceUnitFormModel) => {
        this.subUnitControls.unavailable.visible = true;
        this.disableIfHidden(this.subUnitControls.unavailable, form.controls.unavailable);
      },
    },
    _dataSheet: {
      add: _ => {},
      default: createEmptyFileInputFormValue('drr_ccd_data_sheet'),
      visible: false,
      toggle: (form: FormGroup, value: DeviceUnitFormModel) => {
        if (value?.hasAdditionalHeating != null) {
          this.subUnitControls._dataSheet.visible = value?.hasAdditionalHeating;
          this.removeIfHiddenFileUpload(this.subUnitControls._dataSheet, form, 'dataSheet');
        }
      },
    },
  };

  toggleUnavailable(control: FormControl, isUnavailable: boolean) {
    if (isUnavailable) {
      control.setValue(null);
      control.disable();
    } else {
      control.enable();
    }
  }

  private calculateUnitTotalPower(unitFormControl: FormGroup) {
    let totalPower = 0;
    Object.values(this.unitPowerFieldNames).forEach(field => {
      totalPower += unitFormControl.controls[field]?.value ?? 0;
    });

    const subUnits = unitFormControl.controls.subUnits as FormArray;
    subUnits.controls.forEach((unit: FormGroup) => {
      Object.values(this.subUnitPowerFieldNames).forEach(field => {
        totalPower += unit.controls[field]?.value ?? 0;
      });
    });

    unitFormControl.patchValue({ unitTotalPower: totalPower }, { emitEvent: false });
  }

  private setDisabledForUnit(parentUnit: FormGroup) {
    const subUnits = parentUnit.controls.subUnits as FormArray;
    if (subUnits.controls.some((c: FormGroup) => c.controls.unavailable.value)) {
      parentUnit.controls.unavailable.disable({ emitEvent: false });
    } else {
      parentUnit.controls.unavailable.enable({ emitEvent: false });
    }
  }

  private setDisabledForSubUnits(parentUnavailableValue: boolean, subUnits: FormArray) {
    subUnits.controls.forEach((subUnit: FormGroup) => {
      if (parentUnavailableValue || subUnits.controls.length > 1) {
        subUnit.controls.unavailable.setValue(null);
        subUnit.controls.unavailable.disable({ emitEvent: false });
      } else {
        subUnit.controls.unavailable.enable({ emitEvent: false });
      }
    });
  }

  private removeIfHiddenFileUpload(controlSettings: any, form: FormGroup, controlName: string) {
    const formControl = form.get(controlName) as FormControl;
    if (formControl) {
      if (!controlSettings.visible && formControl.enabled) {
        form.removeControl(controlName);
      } else if (controlSettings.visible && formControl.disabled) {
        formControl.enable();
      }
    }
  }

  private disableIfHidden(controlSettings: any, formControl: AbstractControl) {
    if (!controlSettings.visible && formControl.enabled) {
      formControl.disable();
      formControl.setValue(controlSettings.default ?? null);
    } else if (controlSettings.visible && formControl.disabled) {
      formControl.enable();
    }
  }

  private addAllControls(controls: any, form: FormGroup) {
    Object.values(controls).forEach((control: any) => control.add(form));
  }

  private toggleAllControls(controls: any, targetForm: FormGroup, formValue: any) {
    Object.values(controls).forEach((control: any) => control.toggle(targetForm, formValue));
  }

  private setTranslations() {
    let translations: Translations = new Translations();

    if ([ccdTypes.heatPump].includes(this.ccdType)) {
      translations.unitHeader = this.translation.translateString('drr-ccd-devices.inner-unit-title');
      translations.subUnitHeader = this.translation.translateString('drr-ccd-devices.outer-unit-title');
    }

    if ([ccdTypes.airConditioner].includes(this.ccdType)) {
      translations.unitHeader = this.translation.translateString('drr-ccd-devices.outer-unit-title');
      translations.subUnitHeader = this.translation.translateString('drr-ccd-devices.inner-unit-title');
    }

    if ([ccdTypes.wallBox].includes(this.ccdType)) {
      translations.unitHeader = this.translation.translateString('drr-ccd-devices.charging-infrastructure-title');
    }

    if ([ccdTypes.storage].includes(this.ccdType)) {
      translations.unitHeader = this.translation.translateString('drr-ccd-devices.storage-title');
    }

    this.translations = translations;
  }
}
